/**
 * Calendar Main Page
 *
 * Coordinator component that composes toolbar, sidebar, view area, and all
 * dialog components into the complete calendar experience.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegisterArcheAI } from '@/components/fias-ai';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { api } from '@/lib/api';
import type { SchedulableTaskDetail, AiScheduleSuggestion } from '@/lib/api';
import { useArcheSettings } from '@/lib/hooks/useArcheSettings';
import { ARCHE_ID, ARCHE_PATH } from '../constants';
import { migrateCalendarSettings } from '../settings-migrations';

import type {
  CalendarView,
  CalendarSettings,
  CalendarEventInput,
  EventInstance,
  QuickEventDraft,
  RecurrenceEditScope,
  RecurrenceRule,
} from '../types';
import { DEFAULT_CALENDAR_SETTINGS, getHourHeight } from '../types';
import {
  buildAllDayDraft,
  buildTimedDraft,
  defaultEventStartForDay,
  isDeleteReversible,
  navigateDate,
  occurrenceInstanceDate,
  ruleForNewStart,
  scopeTouchesSeries,
  browserTimeZone,
  seriesOnlyChanges,
  formatList,
} from '../utils';

import { useCalendars } from '../hooks/useCalendars';
import { useEvents } from '../hooks/useEvents';
import { useVisibleRange } from '../hooks/useVisibleRange';
import { useCalendarShortcuts } from '../hooks/useCalendarShortcuts';
import { useCalendarSets } from '../hooks/useCalendarSets';
import { useEventTemplates } from '../hooks/useEventTemplates';
import { useSharedCalendars } from '../hooks/useCalendarSharing';
import { useSchedulingLinks } from '../hooks/useSchedulingLinks';
import { useSchedulableTasks } from '../hooks/useSchedulableTasks';
import { useFocusTime } from '../hooks/useFocusTime';
import { useDailyPlanning } from '../hooks/useDailyPlanning';
import { useCalendarSubscriptions } from '../hooks/useCalendarSubscriptions';
import { useEventSearch } from '../hooks/useEventSearch';
import { useEventDrag } from '../hooks/useEventDrag';
import type { DragDrop } from '../hooks/useEventDrag';
import { useEventResize } from '../hooks/useEventResize';
import { useWeather } from '../hooks/useWeather';
import { useCalendarSyncLive } from '../hooks/useCalendarSyncLive';
import { useIsNarrowViewport } from '../hooks/useIsNarrowViewport';
import { useCalendarAI } from '../hooks/useCalendarAI';

import { CalendarToolbar } from '../components/CalendarToolbar';
import { CalendarSidebar } from '../components/CalendarSidebar';
import { CalendarViewArea } from '../components/CalendarViewArea';
import { CalendarDialogs } from '../components/CalendarDialogs';
import { RecurringScopeDialog } from '../components/RecurringScopeDialog';
import { TaskPanel } from '../components/TaskPanel';
import type { EventDialogSettings } from '../components/EventDialog';

export function CalendarMainPage() {
  const [settings, updateSettings] = useArcheSettings<CalendarSettings>(
    ARCHE_ID,
    DEFAULT_CALENDAR_SETTINGS,
  );

  // Repair settings saved before a default changed. Converges after one pass:
  // the patch stamps the version, after which the migration returns null.
  useEffect(() => {
    const patch = migrateCalendarSettings(settings);
    if (patch) updateSettings(patch);
  }, [settings, updateSettings]);

  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>(settings.defaultView);

  // Turning the Tasks feature off has to take the panel with it, or the panel
  // is stranded on screen with the only control for it gone.
  const showTaskPanel = settings.showTaskPanel && settings.showToolbarTasks;

  // Narrow viewports overlay the left panel instead of docking it, so its
  // open state is per-session there rather than the persisted `sidebarCollapsed`
  // preference — that preference describes the docked layout the user chose on
  // a big screen and should survive a trip through a phone.
  const isNarrowViewport = useIsNarrowViewport();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Data
  const {
    calendars,
    loading: calendarsLoading,
    error: calendarsError,
    fetchCalendars,
    createCalendar,
    deleteCalendar,
    toggleVisibility,
    visibleCalendarIds,
  } = useCalendars();

  const range = useVisibleRange(currentDate, view, settings);
  const {
    events,
    loading: eventsLoading,
    refetch: refetchEvents,
  } = useEvents(range.start, range.end);

  // Fixed date ranges for daily planning (independent of navigation)
  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const todayEnd = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);
  const yesterdayStart = useMemo(() => {
    const d = new Date(todayStart);
    d.setDate(d.getDate() - 1);
    return d;
  }, [todayStart]);
  const yesterdayEnd = useMemo(() => {
    const d = new Date(todayEnd);
    d.setDate(d.getDate() - 1);
    return d;
  }, [todayEnd]);

  const { events: todayEventsRaw } = useEvents(todayStart, todayEnd);
  const { events: yesterdayEventsRaw } = useEvents(yesterdayStart, yesterdayEnd);

  // Provider sync applied remote changes (phone-side edits) — refetch the
  // visible range so they appear within seconds of the sync run.
  useCalendarSyncLive(() => {
    refetchEvents();
    fetchCalendars();
  });

  // Calendar Sets
  const {
    calendarSets,
    activeCalendarSetId,
    createSet,
    renameSet,
    deleteSet: deleteCalendarSet,
    updateSetCalendars,
    activateSet,
    deactivateSet,
  } = useCalendarSets({
    settings,
    updateSettings,
    calendars,
    toggleVisibility,
  });

  // Event Templates
  const { templates, createTemplate, updateTemplate, deleteTemplate, createTemplateFromEvent } =
    useEventTemplates();

  // Shared Calendars
  const { sharedCalendars } = useSharedCalendars();

  // Scheduling Links
  const {
    schedulingLinks,
    createLink: createSchedulingLink,
    updateLink: updateSchedulingLink,
    deleteLink: deleteSchedulingLink,
    fetchBookings: fetchSchedulingLinkBookings,
    cancelBooking: cancelSchedulingLinkBooking,
  } = useSchedulingLinks();

  // Schedulable Tasks
  const {
    tasks,
    fetchTasks,
    createTask,
    updateTask,
    suggestSchedule,
    acceptSuggestion,
    completeTask,
  } = useSchedulableTasks();

  // Focus Time
  const {
    settings: focusTimeSettings,
    updateSettings: updateFocusTimeSettings,
    generate: generateFocusBlocks,
    regenerate: regenerateFocusBlocks,
    clear: clearFocusBlocks,
  } = useFocusTime();

  // Calendar Subscriptions
  const {
    subscriptions,
    subscribe: subscribeToFeed,
    refresh: refreshSubscription,
    awaitFetch: awaitFeedFetch,
    unsubscribe,
  } = useCalendarSubscriptions();

  // Event Search
  const search = useEventSearch();

  const handleSearchSelect = useCallback((event: import('@/lib/api').CalendarEventInstance) => {
    setCurrentDate(new Date(event.startAt));
    setSelectedEvent(event);
    setDetailDialogOpen(true);
  }, []);

  // Held while the scope chooser is open for a grid resize or drag of a series.
  const [pendingResize, setPendingResize] = useState<{
    event: EventInstance;
    endAt: string;
  } | null>(null);
  const [pendingMove, setPendingMove] = useState<{
    event: EventInstance;
    startAt: string;
    endAt: string;
  } | null>(null);

  // Event Resize
  const applyResize = useCallback(
    async (event: EventInstance, endAt: string, scope: RecurrenceEditScope) => {
      try {
        await api.updateCalendarEvent(event.eventId, {
          endAt,
          scope,
          // Undefined here made the server reject the whole request with
          // "instanceDate required for scope='this'"; non-recurring events
          // ignore the field entirely.
          ...(scope !== 'all' ? { instanceDate: occurrenceInstanceDate(event) } : {}),
        });
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to resize event',
          variant: 'destructive',
        });
      }
    },
    [refetchEvents],
  );

  /**
   * Resizing a recurring event asks which occurrences it applies to, exactly as
   * editing and deleting do.
   *
   * It used to send a hardcoded `scope: 'this'` with no prompt. That is the
   * safe direction — unlike the edit dialog's old hardcoded `'all'` it could
   * not rewrite a series — but it left one decision answered two different ways
   * in the same app, and a user who wanted to lengthen every occurrence of a
   * standing meeting had no way to say so from the grid.
   */
  const handleResizeEvent = useCallback(
    async (instanceId: string, newDurationMinutes: number) => {
      // Keyed by INSTANCE id. Matching on eventId returned the first
      // occurrence of a series regardless of which one was dragged.
      const event = events.find((e) => e.id === instanceId);
      if (!event) return;
      const startAt = new Date(event.startAt);
      const endAt = new Date(startAt.getTime() + newDurationMinutes * 60 * 1000).toISOString();

      if (event.isRecurring) {
        setPendingResize({ event, endAt });
        return;
      }
      await applyResize(event, endAt, 'all');
    },
    [events, applyResize],
  );

  const applyMove = useCallback(
    async (event: EventInstance, startAt: string, endAt: string, scope: RecurrenceEditScope) => {
      try {
        // Moving the series must carry its RULE along. A weekly master left
        // starting on a day its own BYDAY excludes is rejected by Graph; a
        // MONTHLY one is worse — Graph accepts the start-only change with 200
        // and silently discards it, so the move is lost and then reverted
        // locally by the next pull.
        // `this_and_future` overwrites the series rrule too, so it counts;
        // testing `scope === 'all'` here disagreed with the dialog, which
        // already knew that. Shared predicate so they cannot drift again.
        const adjusted = scopeTouchesSeries(scope)
          ? ruleForNewStart(event.rrule, startAt, {
              timeZone: browserTimeZone(),
              previousStartIso: event.startAt,
              isAllDay: event.isAllDay,
            })
          : ({ kind: 'unchanged' } as const);

        if (adjusted.kind === 'unsupported') {
          // Refuse rather than submit a start the rule contradicts. refetch
          // puts the dragged event back where it was.
          toast({
            title: 'Cannot move the whole series',
            description: adjusted.reason,
            variant: 'destructive',
          });
          refetchEvents();
          return;
        }

        await api.updateCalendarEvent(event.eventId, {
          startAt,
          endAt,
          scope,
          ...(adjusted.kind === 'rewritten' ? { rrule: adjusted.rrule } : {}),
          ...(scope !== 'all' ? { instanceDate: occurrenceInstanceDate(event) } : {}),
        });
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to move event',
          variant: 'destructive',
        });
      }
    },
    [refetchEvents],
  );

  /**
   * Drop handler for dragging an event to a new time or day.
   *
   * The duration is carried across rather than recomputed, for the same reason
   * the dialog's start handler shifts the end instead of applying a default: a
   * deliberately long or short event must keep its length when it is merely
   * rescheduled.
   *
   * `targetDayKey` is null when the drop landed outside any day column, which
   * means the day is unchanged — the vertical part of the gesture still counts.
   */
  const handleMoveEvent = useCallback(
    async (instanceId: string, drop: DragDrop) => {
      const event = events.find((e) => e.id === instanceId);
      if (!event) return;

      const start = new Date(event.startAt);
      // Some providers emit end == start for a single all-day event — the shape
      // `eventOccupiesDay` already floors at one day so it renders at all.
      // Without the same floor here a drag sent endAt === startAt and the
      // server refused it with "end must be after start".
      const rawDurationMs = new Date(event.endAt).getTime() - start.getTime();
      const durationMs =
        event.isAllDay && rawDurationMs <= 0 ? 86_400_000 : Math.max(rawDurationMs, 0);

      // Days moved is the difference between where the grabbed SEGMENT sat and
      // where it was dropped — never the drop date applied to the event's
      // start. A multi-day or overnight event is drawn in every day it
      // overlaps, all blocks sharing one id, so using the drop date directly
      // moved the event by however far into it the user had grabbed: the
      // Tuesday half of a Mon->Tue night shift jumped a whole day, and a
      // five-day event grabbed on day three and nudged one day jumped three.
      const dayDelta =
        drop.originDayKey && drop.targetDayKey
          ? Math.round(
              (Date.parse(`${drop.targetDayKey}T00:00:00Z`) -
                Date.parse(`${drop.originDayKey}T00:00:00Z`)) /
                86_400_000,
            )
          : 0;

      let next: Date;
      if (event.isAllDay) {
        // All-day events are DATES at UTC midnight, so they move by whole days
        // in UTC. Going through local time would re-introduce the off-by-one
        // that encodeAllDayRange exists to prevent.
        if (dayDelta === 0) return;
        next = new Date(start.getTime() + dayDelta * 86_400_000);
      } else {
        next = new Date(start.getTime() + drop.minuteDelta * 60_000);
        if (dayDelta !== 0) next.setDate(next.getDate() + dayDelta);
        if (next.getTime() === start.getTime()) return;
      }

      const startAt = next.toISOString();
      const endAt = new Date(next.getTime() + durationMs).toISOString();

      if (event.isRecurring) {
        setPendingMove({ event, startAt, endAt });
        return;
      }
      await applyMove(event, startAt, endAt, 'all');
    },
    [events, applyMove],
  );

  const hourHeightPx = getHourHeight(settings.timeGridDensity);
  const { resizingEventId, resizeHeight, onResizeStart } = useEventResize(
    handleResizeEvent,
    hourHeightPx,
    settings.snapIntervalMinutes,
  );
  const {
    draggingEventId,
    dragOffset,
    onDragStart: onEventDragStart,
  } = useEventDrag(handleMoveEvent, hourHeightPx, settings.snapIntervalMinutes);

  // Month view: same drop handler, but its vertical axis is weeks rather than
  // hours, so the gesture changes the DAY and leaves the time of day alone.
  const { draggingEventId: monthDraggingEventId, onDragStart: onMonthEventDragStart } =
    useEventDrag(handleMoveEvent, hourHeightPx, settings.snapIntervalMinutes, { dayOnly: true });

  // Weather
  const weather = useWeather(settings.showWeather ?? true);

  // Daily Planning
  const dailyPlanning = useDailyPlanning({
    settings,
    updateSettings,
    todayEvents: todayEventsRaw,
    yesterdayEvents: yesterdayEventsRaw,
    tasks,
  });

  const [dailyPlanningOpen, setDailyPlanningOpen] = useState(false);
  const [eveningReviewOpen, setEveningReviewOpen] = useState(false);
  const [focusTimeDialogOpen, setFocusTimeDialogOpen] = useState(false);

  // Auto-trigger morning wizard (800ms delay after data loads)
  const morningWizardTriggered = useRef(false);
  useEffect(() => {
    if (
      dailyPlanning.shouldShowMorningWizard &&
      !morningWizardTriggered.current &&
      !calendarsLoading &&
      !eventsLoading
    ) {
      morningWizardTriggered.current = true;
      const timer = setTimeout(() => setDailyPlanningOpen(true), 800);
      return () => clearTimeout(timer);
    }
  }, [dailyPlanning.shouldShowMorningWizard, calendarsLoading, eventsLoading]);

  // Auto-trigger evening review (800ms delay after data loads)
  const eveningReviewTriggered = useRef(false);
  useEffect(() => {
    if (
      dailyPlanning.shouldShowEveningReview &&
      !eveningReviewTriggered.current &&
      !calendarsLoading &&
      !eventsLoading
    ) {
      eveningReviewTriggered.current = true;
      const timer = setTimeout(() => setEveningReviewOpen(true), 800);
      return () => clearTimeout(timer);
    }
  }, [dailyPlanning.shouldShowEveningReview, calendarsLoading, eventsLoading]);

  // Filter events by visible calendars
  const visibleEvents = useMemo(
    () => events.filter((e) => visibleCalendarIds.includes(e.calendarId)),
    [events, visibleCalendarIds],
  );

  // Dialog state
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [quickEventOpen, setQuickEventOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [calendarFormOpen, setCalendarFormOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
  const [calendarSetManagerOpen, setCalendarSetManagerOpen] = useState(false);
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareCalendarTarget, setShareCalendarTarget] = useState<string | null>(null);
  const [publishFeedDialogOpen, setPublishFeedDialogOpen] = useState(false);
  const [publishFeedTarget, setPublishFeedTarget] = useState<string | null>(null);
  const [findTimeOpen, setFindTimeOpen] = useState(false);
  const [schedulingLinksOpen, setSchedulingLinksOpen] = useState(false);
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);

  // Task dialog state
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editTaskTarget, setEditTaskTarget] = useState<SchedulableTaskDetail | null>(null);
  const [suggestionsDialogOpen, setSuggestionsDialogOpen] = useState(false);
  const [suggestionsTask, setSuggestionsTask] = useState<SchedulableTaskDetail | null>(null);
  const [suggestions, setSuggestions] = useState<AiScheduleSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const [selectedEvent, setSelectedEvent] = useState<EventInstance | null>(null);
  const [editEvent, setEditEvent] = useState<EventInstance | null>(null);
  const [quickEventDraft, setQuickEventDraft] = useState<QuickEventDraft>(() =>
    buildTimedDraft(new Date(), DEFAULT_CALENDAR_SETTINGS.defaultEventDuration),
  );
  const [initialEventData, setInitialEventData] = useState<Partial<CalendarEventInput>>({});

  // Navigation
  const handlePrev = useCallback(() => {
    setCurrentDate((d) => navigateDate(d, view, 'prev'));
  }, [view]);

  const handleNext = useCallback(() => {
    setCurrentDate((d) => navigateDate(d, view, 'next'));
  }, [view]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const handleViewChange = useCallback((v: CalendarView) => {
    setView(v);
  }, []);

  const handleGoToDate = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  // Event interactions
  const handleClickEvent = useCallback((event: EventInstance) => {
    setSelectedEvent(event);
    setDetailDialogOpen(true);
  }, []);

  // The one place month view still navigates: the "+N more" control, where
  // drilling into the day IS the request.
  const handleShowMore = useCallback((date: Date) => {
    setCurrentDate(date);
    setView('day');
  }, []);

  // Month: a drag across days makes an all-day event spanning them, the way
  // a multi-day block reads on a month grid.
  const handleSelectDays = useCallback((from: Date, to: Date) => {
    setQuickEventDraft(buildAllDayDraft(from, to));
    setQuickEventOpen(true);
  }, []);

  // Week/Day: a click gets the default duration, a drag gets the range it
  // covered — the grid has already resolved which of the two happened.
  const handleSelectRange = useCallback((start: Date, end: Date) => {
    setQuickEventDraft({ start, end, isAllDay: false });
    setQuickEventOpen(true);
  }, []);

  // Month view: clicking a day creates an event on it. It deliberately does
  // NOT jump to day view — navigating is what the mini month and the date
  // label are for, and stealing the click made every mis-aimed tap a
  // navigation the user then had to undo.
  const handleClickDate = useCallback(
    (date: Date) => {
      const start = defaultEventStartForDay(
        date,
        settings.workingHoursStart,
        settings.snapIntervalMinutes,
      );
      setQuickEventDraft(buildTimedDraft(start, settings.defaultEventDuration));
      setQuickEventOpen(true);
    },
    [settings.workingHoursStart, settings.snapIntervalMinutes, settings.defaultEventDuration],
  );

  const handleNewEvent = useCallback(() => {
    setEditEvent(null);
    setInitialEventData({});
    setEventDialogOpen(true);
  }, []);

  // CRUD operations
  const handleSaveEvent = useCallback(
    async (
      input: CalendarEventInput & {
        rruleObj?: RecurrenceRule | null;
        scope?: RecurrenceEditScope;
      },
    ) => {
      const { rruleObj: _rruleObj, scope, ...eventInput } = input;

      try {
        if (editEvent) {
          // The scope comes from the dialog's chooser. It used to be hardcoded
          // to 'all' with no prompt, so editing ONE occurrence of a series
          // rewrote every occurrence — a silent, hard-to-undo data change.
          // A non-recurring event has nothing to scope and falls back to 'all',
          // which for a single event means the event itself.
          const effectiveScope: RecurrenceEditScope = scope ?? 'all';

          // A single-occurrence edit is stored as an EXCEPTION row, which has
          // columns for only part of an event. Anything else silently vanished
          // while the UI said "Event updated" — the user pasted a link onto one
          // occurrence, got a success toast, and the link was never saved.
          // Refuse instead, and name what needs the whole series.
          const seriesOnly = seriesOnlyChanges(
            editEvent as unknown as Record<string, unknown>,
            eventInput as unknown as Record<string, unknown>,
          );
          // ONLY 'this'. A `this_and_future` edit does not write an exception —
          // `splitSeriesInTx` creates a new series row and persists calendarId,
          // timezone, url, tags and rrule verbatim. Refusing it sent a user
          // wanting a new link 'from next week on' to "All events", which
          // rewrites every PAST occurrence too: the wrong data.
          if (effectiveScope === 'this' && seriesOnly.length > 0) {
            toast({
              title: `Changing ${formatList(seriesOnly)} applies to the whole series`,
              description: 'Choose "All events" to save this change.',
              variant: 'destructive',
            });
            return;
          }

          await api.updateCalendarEvent(editEvent.eventId, {
            ...eventInput,
            scope: effectiveScope,
            // Required by the server for 'this' / 'this_and_future' — it names
            // WHICH occurrence the edit is anchored to. Same helper the delete
            // path uses, so the two agree on how an occurrence is identified.
            ...(effectiveScope !== 'all'
              ? { instanceDate: occurrenceInstanceDate(editEvent) }
              : {}),
          });
          toast({ title: 'Event updated' });
        } else {
          await api.createCalendarEvent(eventInput);
          toast({ title: 'Event created' });
        }
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to save event',
          variant: 'destructive',
        });
      }
    },
    [editEvent, refetchEvents],
  );

  const handleQuickSave = useCallback(
    async (title: string) => {
      const { start: startAt, end: endAt, isAllDay } = quickEventDraft;

      const defaultCalendar = calendars.find((c) => c.isDefault) || calendars[0];
      if (!defaultCalendar) {
        toast({ title: 'No calendar available', variant: 'destructive' });
        return;
      }

      try {
        await api.createCalendarEvent({
          calendarId: defaultCalendar.id,
          title,
          startAt: startAt.toISOString(),
          endAt: endAt.toISOString(),
          isAllDay,
          status: settings.defaultEventStatus,
          color: settings.defaultEventColor,
          reminders:
            settings.defaultEventReminder != null
              ? [{ type: 'notification', minutesBefore: settings.defaultEventReminder }]
              : undefined,
        });
        toast({ title: 'Event created' });
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to create event',
          variant: 'destructive',
        });
      }
    },
    [
      quickEventDraft,
      calendars,
      refetchEvents,
      settings.defaultEventStatus,
      settings.defaultEventColor,
      settings.defaultEventReminder,
    ],
  );

  const handleEditEvent = useCallback((event: EventInstance) => {
    setEditEvent(event);
    setEventDialogOpen(true);
  }, []);

  const handleDeleteEvent = useCallback(
    async (event: EventInstance, scope: RecurrenceEditScope) => {
      try {
        await api.deleteCalendarEvent(event.eventId, scope, occurrenceInstanceDate(event));

        // Undo is offered ONLY where restore-by-id genuinely reverses the
        // delete — see isDeleteReversible for which shapes those are and why
        // the others must not get a button.
        const isReversible = isDeleteReversible(event, scope);

        toast({
          title: 'Event deleted',
          ...(isReversible && {
            action: (
              <ToastAction
                altText="Undo deleting this event"
                onClick={() => {
                  void (async () => {
                    try {
                      // Only whole-event restores are offered — see
                      // isDeleteReversible for why single occurrences are not.
                      await api.restoreCalendarEvent(event.eventId);
                      refetchEvents();
                      toast({ title: 'Event restored' });
                    } catch (err) {
                      toast({
                        title: 'Could not restore event',
                        description: err instanceof Error ? err.message : undefined,
                        variant: 'destructive',
                      });
                    }
                  })();
                }}
              >
                Undo
              </ToastAction>
            ),
          }),
        });
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to delete',
          variant: 'destructive',
        });
      }
    },
    [refetchEvents],
  );

  const handleDeleteSearchEvents = useCallback(
    async (eventIds: string[]) => {
      let deleted = 0;
      for (const id of eventIds) {
        try {
          await api.deleteCalendarEvent(id, 'all');
          deleted++;
        } catch {
          // continue deleting remaining
        }
      }
      refetchEvents();
      search.setQuery('');
      toast({
        title: `Deleted ${deleted} event${deleted === 1 ? '' : 's'}`,
        ...(deleted < eventIds.length && {
          description: `${eventIds.length - deleted} failed to delete`,
          variant: 'destructive' as const,
        }),
      });
    },
    [refetchEvents, search],
  );

  const handleSaveCalendar = useCallback(
    async (input: { name: string; description?: string; color?: string }) => {
      await createCalendar(input);
      toast({ title: 'Calendar created' });
    },
    [createCalendar],
  );

  const handleDeleteCalendar = useCallback(
    async (id: string) => {
      try {
        await deleteCalendar(id);
        toast({ title: 'Calendar deleted' });
        refetchEvents();
      } catch (err) {
        // The non-empty guard is a safety check, not a dead end: offer the
        // cascade rather than leaving the calendar permanently undeletable.
        const message = err instanceof Error ? err.message : 'Failed to delete calendar';
        if (message.includes('CALENDAR_NOT_EMPTY')) {
          const name = calendars.find((c) => c.id === id)?.name ?? 'this calendar';
          if (
            window.confirm(
              `"${name}" still has events.\n\n` +
                'OK — delete the calendar AND its events.\nCancel — keep everything.',
            )
          ) {
            try {
              await deleteCalendar(id, true);
              toast({ title: 'Calendar and its events deleted' });
              refetchEvents();
              return;
            } catch (cascadeErr) {
              toast({
                title: 'Error',
                description:
                  cascadeErr instanceof Error ? cascadeErr.message : 'Failed to delete calendar',
                variant: 'destructive',
              });
              return;
            }
          }
          return;
        }
        toast({ title: 'Error', description: message, variant: 'destructive' });
      }
    },
    [deleteCalendar, refetchEvents, calendars],
  );

  // The feed itself is fetched by the worker, so both handlers wait for
  // that fetch to land before refetching events — otherwise the calendar
  // repaints empty and the feed's events appear only on the next reload.
  const handleSubscribe = useCallback(
    async (feedUrl: string, name?: string) => {
      let created;
      try {
        created = await subscribeToFeed(feedUrl, name);
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to subscribe',
          variant: 'destructive',
        });
        throw err;
      }
      toast({ title: 'Subscribed to feed', description: 'Fetching events…' });
      fetchCalendars();

      const settled = await awaitFeedFetch(created.id, created.lastFetchedAt);
      if (settled?.status === 'error') {
        toast({
          title: 'Feed could not be fetched',
          description: settled.errorMessage ?? 'The feed did not return a valid calendar',
          variant: 'destructive',
        });
      }
      fetchCalendars();
      refetchEvents();
    },
    [subscribeToFeed, awaitFeedFetch, fetchCalendars, refetchEvents],
  );

  const handleRefreshSubscription = useCallback(
    async (id: string) => {
      let subscription;
      try {
        subscription = await refreshSubscription(id);
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to refresh',
          variant: 'destructive',
        });
        return;
      }
      const settled = await awaitFeedFetch(id, subscription.lastFetchedAt);
      if (settled?.status === 'error') {
        toast({
          title: 'Feed could not be fetched',
          description: settled.errorMessage ?? 'The feed did not return a valid calendar',
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Subscription refreshed' });
      }
      refetchEvents();
    },
    [refreshSubscription, awaitFeedFetch, refetchEvents],
  );

  const handleUnsubscribe = useCallback(
    async (id: string) => {
      try {
        // Unsubscribing used to strand every event the feed imported, and the
        // calendar then refused to delete (CALENDAR_NOT_EMPTY) — no cleanup
        // path existed. Ask, the same way disconnect does.
        let removable = 0;
        try {
          removable = (await api.calendarSubscriptionDeletePreview(id)).removable;
        } catch {
          // Preview is best-effort — fall through to a plain unsubscribe.
        }
        const remove =
          removable > 0 &&
          window.confirm(
            `Also delete the ${removable} event(s) this feed added?\n\n` +
              'OK — delete them.\nCancel — keep them in the calendar.',
          );
        const res = await unsubscribe(id, remove ? 'remove' : 'keep');
        toast({
          title: 'Unsubscribed',
          description: res?.removed ? `${res.removed} event(s) removed.` : undefined,
        });
        fetchCalendars();
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to unsubscribe',
          variant: 'destructive',
        });
      }
    },
    [unsubscribe, fetchCalendars, refetchEvents],
  );

  const handleShareCalendar = useCallback((calendarId: string) => {
    setShareCalendarTarget(calendarId);
    setShareDialogOpen(true);
  }, []);

  const handlePublishFeed = useCallback((calendarId: string) => {
    setPublishFeedTarget(calendarId);
    setPublishFeedDialogOpen(true);
  }, []);

  const handleFindTimeSelectSlot = useCallback((startAt: string, endAt: string) => {
    setInitialEventData({ startAt, endAt });
    setEditEvent(null);
    setEventDialogOpen(true);
  }, []);

  const handleQuickExpand = useCallback(
    (parsedData?: Partial<CalendarEventInput>) => {
      setQuickEventOpen(false);
      setInitialEventData({
        startAt: quickEventDraft.start.toISOString(),
        endAt: quickEventDraft.end.toISOString(),
        isAllDay: quickEventDraft.isAllDay,
        ...parsedData,
      });
      setEditEvent(null);
      setEventDialogOpen(true);
    },
    [quickEventDraft],
  );

  const handleSaveAsTemplate = useCallback(
    async (name: string) => {
      if (!editEvent) return;
      try {
        await createTemplateFromEvent(editEvent, name);
        toast({ title: 'Template saved' });
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to save template',
          variant: 'destructive',
        });
      }
    },
    [editEvent, createTemplateFromEvent],
  );

  // Task handlers
  const handleAutoSchedule = useCallback(
    async (task: SchedulableTaskDetail) => {
      setSuggestionsTask(task);
      setSuggestions([]);
      setSuggestionsDialogOpen(true);
      setSuggestionsLoading(true);
      try {
        const result = await suggestSchedule(task.id, {
          workingHoursStart: settings.workingHoursStart,
          workingHoursEnd: settings.workingHoursEnd,
          timezone:
            settings.timezone === 'local'
              ? Intl.DateTimeFormat().resolvedOptions().timeZone
              : settings.timezone,
        });
        setSuggestions(result);
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to get suggestions',
          variant: 'destructive',
        });
      } finally {
        setSuggestionsLoading(false);
      }
    },
    [suggestSchedule, settings],
  );

  const handleAcceptSuggestion = useCallback(
    async (index: number) => {
      if (!suggestionsTask) return;
      try {
        await acceptSuggestion(suggestionsTask.id, index);
        toast({ title: 'Task scheduled' });
        setSuggestionsDialogOpen(false);
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to accept suggestion',
          variant: 'destructive',
        });
      }
    },
    [suggestionsTask, acceptSuggestion, refetchEvents],
  );

  const handleCompleteTask = useCallback(
    async (taskId: string) => {
      try {
        await completeTask(taskId);
        toast({ title: 'Task completed' });
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to complete task',
          variant: 'destructive',
        });
      }
    },
    [completeTask],
  );

  const handleClickTask = useCallback((task: SchedulableTaskDetail) => {
    setEditTaskTarget(task);
    setTaskDialogOpen(true);
  }, []);

  const handleSaveTask = useCallback(
    async (input: import('@/lib/api').SchedulableTaskInput) => {
      try {
        if (editTaskTarget) {
          await updateTask(editTaskTarget.id, input);
          toast({ title: 'Task updated' });
        } else {
          await createTask(input);
          toast({ title: 'Task created' });
        }
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to save task',
          variant: 'destructive',
        });
        throw err;
      }
    },
    [editTaskTarget, createTask, updateTask],
  );

  const handleToggleTaskPanel = useCallback(() => {
    updateSettings({ showTaskPanel: !settings.showTaskPanel });
  }, [settings.showTaskPanel, updateSettings]);

  const handleToggleSidebar = useCallback(() => {
    if (isNarrowViewport) {
      setMobileSidebarOpen((open) => !open);
      return;
    }
    updateSettings({ sidebarCollapsed: !settings.sidebarCollapsed });
  }, [isNarrowViewport, settings.sidebarCollapsed, updateSettings]);

  const handleFocusBlocksAction = useCallback(
    async (action: () => Promise<number>) => {
      const result = await action();
      refetchEvents();
      return result;
    },
    [refetchEvents],
  );

  const handleScheduleTaskToTime = useCallback(
    async (taskId: string, hour: number, durationMinutes: number) => {
      const defaultCalendar = calendars.find((c) => c.isDefault) || calendars[0];
      if (!defaultCalendar) return;

      const task = tasks.find((t) => t.id === taskId);
      if (!task) return;

      const startAt = new Date();
      startAt.setHours(hour, 0, 0, 0);
      const endAt = new Date(startAt.getTime() + durationMinutes * 60000);

      try {
        await api.createCalendarEvent({
          calendarId: defaultCalendar.id,
          title: task.title,
          description: task.description || undefined,
          startAt: startAt.toISOString(),
          endAt: endAt.toISOString(),
        });
        refetchEvents();
        toast({ title: 'Task scheduled' });
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to schedule task',
          variant: 'destructive',
        });
      }
    },
    [calendars, tasks, refetchEvents],
  );

  // Keyboard shortcuts -- placed after all handlers so refs capture latest values
  const shortcutActionsRef = useRef({
    onNewEvent: handleNewEvent,
    onToday: handleToday,
    onPrev: handlePrev,
    onNext: handleNext,
    onViewChange: handleViewChange,
    onOpenCommandPalette: () => setCommandPaletteOpen(true),
    onOpenShortcutHelp: () => setShortcutHelpOpen(true),
  });
  shortcutActionsRef.current = {
    onNewEvent: handleNewEvent,
    onToday: handleToday,
    onPrev: handlePrev,
    onNext: handleNext,
    onViewChange: handleViewChange,
    onOpenCommandPalette: () => setCommandPaletteOpen(true),
    onOpenShortcutHelp: () => setShortcutHelpOpen(true),
  };

  const stableShortcutActions = useMemo(
    () => ({
      onNewEvent: () => shortcutActionsRef.current.onNewEvent(),
      onToday: () => shortcutActionsRef.current.onToday(),
      onPrev: () => shortcutActionsRef.current.onPrev(),
      onNext: () => shortcutActionsRef.current.onNext(),
      onViewChange: (v: CalendarView) => shortcutActionsRef.current.onViewChange(v),
      onOpenCommandPalette: () => shortcutActionsRef.current.onOpenCommandPalette(),
      onOpenShortcutHelp: () => shortcutActionsRef.current.onOpenShortcutHelp(),
    }),
    [],
  );
  useCalendarShortcuts(stableShortcutActions);

  // Meeting count warning for toolbar
  const meetingWarning = useMemo(() => {
    if (settings.maxMeetingsPerDay == null) return null;
    const todayCount = todayEventsRaw.filter((e) => !e.isAllDay).length;
    if (todayCount >= settings.maxMeetingsPerDay) {
      return { count: todayCount, max: settings.maxMeetingsPerDay };
    }
    return null;
  }, [todayEventsRaw, settings.maxMeetingsPerDay]);

  const loading = calendarsLoading || eventsLoading;

  // Grouped settings for EventDialog (I11)
  const eventDialogSettings: EventDialogSettings = useMemo(
    () => ({
      defaultCalendarId: settings.defaultCalendarId || calendars[0]?.id || null,
      defaultEventDuration: settings.defaultEventDuration,
      defaultEventReminder: settings.defaultEventReminder,
      defaultEventStatus: settings.defaultEventStatus,
      defaultEventColor: settings.defaultEventColor,
      showConflictWarnings: settings.showConflictWarnings,
      allowDoubleBooking: settings.allowDoubleBooking,
      timeFormat: settings.timeFormat,
      minBreakBetweenEvents: settings.minBreakBetweenEvents,
    }),
    [
      settings.defaultCalendarId,
      settings.defaultEventDuration,
      settings.defaultEventReminder,
      settings.defaultEventStatus,
      settings.defaultEventColor,
      settings.showConflictWarnings,
      settings.allowDoubleBooking,
      settings.timeFormat,
      settings.minBreakBetweenEvents,
      calendars,
    ],
  );

  const handleNewEventFromTemplate = useCallback(
    (template: import('@/lib/api').CalendarEventTemplateDetail) => {
      const now = new Date();
      const end = new Date(now.getTime() + (template.durationMinutes || 60) * 60000);
      setInitialEventData({
        title: template.title,
        description: template.description || undefined,
        location: template.location || undefined,
        startAt: now.toISOString(),
        endAt: end.toISOString(),
        isAllDay: template.isAllDay,
        status: template.status,
        color: template.color,
        url: template.url || undefined,
        calendarId: template.calendarId || undefined,
        isRecurring: template.isRecurring,
        rrule: template.rrule || undefined,
      });
      setEditEvent(null);
      setEventDialogOpen(true);
    },
    [],
  );

  // --- Fias AI ---------------------------------------------------------------
  // The UI plane only: view/date/dialogs/preferences. Reading and writing
  // calendar DATA is the calendar_* server tools' job, and they work whether or
  // not this page is mounted — see hooks/useCalendarAI.ts.
  const openEventComposer = useCallback((initial: Partial<CalendarEventInput>) => {
    setEditEvent(null);
    setInitialEventData(initial);
    setEventDialogOpen(true);
  }, []);

  useRegisterArcheAI(
    useCalendarAI({
      settings,
      updateSettings,
      view,
      setView,
      currentDate,
      setCurrentDate,
      visibleRange: range,
      calendars,
      visibleEvents,
      tasks,
      search: { query: search.query, setQuery: search.setQuery, clear: search.clear },
      sidebarVisible: isNarrowViewport ? mobileSidebarOpen : !settings.sidebarCollapsed,
      onToggleSidebar: handleToggleSidebar,
      openEventComposer,
      openFindTime: () => setFindTimeOpen(true),
      openSchedulingLinks: () => setSchedulingLinksOpen(true),
      openDailyPlanning: () => setDailyPlanningOpen(true),
      openEveningReview: () => setEveningReviewOpen(true),
      openFocusTime: () => setFocusTimeDialogOpen(true),
      openImport: () => setImportDialogOpen(true),
      refetchEvents,
      refetchTasks: fetchTasks,
    }),
  );

  const sidebar = (
    <CalendarSidebar
      currentDate={currentDate}
      selectedDate={currentDate}
      weekStartDay={settings.weekStartDay}
      calendars={calendars}
      onSelectDate={setCurrentDate}
      onToggleCalendar={toggleVisibility}
      onAddCalendar={() => setCalendarFormOpen(true)}
      onDeleteCalendar={handleDeleteCalendar}
      onShareCalendar={handleShareCalendar}
      onPublishFeed={handlePublishFeed}
      sharedCalendars={sharedCalendars}
      subscriptions={subscriptions}
      onSubscribe={() => setSubscribeDialogOpen(true)}
      onRefreshSubscription={handleRefreshSubscription}
      onUnsubscribe={handleUnsubscribe}
      calendarSets={calendarSets}
      activeCalendarSetId={activeCalendarSetId}
      onActivateSet={activateSet}
      onDeactivateSet={deactivateSet}
      onManageSets={() => setCalendarSetManagerOpen(true)}
      taskPanel={
        showTaskPanel ? (
          <TaskPanel
            tasks={tasks}
            onNewTask={() => {
              setEditTaskTarget(null);
              setTaskDialogOpen(true);
            }}
            onAutoSchedule={handleAutoSchedule}
            onCompleteTask={handleCompleteTask}
            onClickTask={handleClickTask}
            taskSortOrder={settings.taskSortOrder}
            showCompletedTasks={settings.showCompletedTasks}
          />
        ) : undefined
      }
    />
  );

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <CalendarToolbar
        currentDate={currentDate}
        view={view}
        sidebarCollapsed={isNarrowViewport ? !mobileSidebarOpen : settings.sidebarCollapsed}
        onToggleSidebar={handleToggleSidebar}
        onViewChange={handleViewChange}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
        onNewEvent={handleNewEvent}
        // Every feature toggle is applied the same way: withhold the handler
        // and the toolbar drops the action, from both the icon row and the
        // overflow menu. Settings and Features are never gated — they are the
        // way back.
        onImport={settings.showToolbarImport ? () => setImportDialogOpen(true) : undefined}
        onSettings={() => setSettingsOpen(true)}
        onFeatures={() => setFeaturesOpen(true)}
        onAnalytics={
          settings.showToolbarAnalytics ? () => navigate(`${ARCHE_PATH}/analytics`) : undefined
        }
        onFindTime={settings.showToolbarFindTime ? () => setFindTimeOpen(true) : undefined}
        onSchedulingLinks={
          settings.showToolbarSchedulingLinks ? () => setSchedulingLinksOpen(true) : undefined
        }
        onToggleTasks={settings.showToolbarTasks ? handleToggleTaskPanel : undefined}
        showTaskPanel={showTaskPanel}
        onDailyPlanning={
          settings.dailyPlanningEnabled ? () => setDailyPlanningOpen(true) : undefined
        }
        onEveningReview={
          settings.eveningReviewEnabled ? () => setEveningReviewOpen(true) : undefined
        }
        onFocusTime={settings.showToolbarFocusTime ? () => setFocusTimeDialogOpen(true) : undefined}
        searchQuery={search.query}
        onSearchQueryChange={search.setQuery}
        searchResults={search.results}
        searchLoading={search.loading}
        onSearchSelect={handleSearchSelect}
        onSearchDeleteEvents={handleDeleteSearchEvents}
        onSearchClear={search.clear}
        timeFormat={settings.timeFormat}
        meetingWarning={meetingWarning}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — beside the calendar when there is room, over it when
            there is not. On a phone an inline panel takes most of the viewport
            and leaves the calendar rendering into what is left. */}
        {!isNarrowViewport && !settings.sidebarCollapsed && sidebar}

        {isNarrowViewport && (
          <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
            <SheetContent side="left" className="w-[17rem] max-w-[85vw] p-0 pt-10">
              <SheetHeader className="sr-only">
                <SheetTitle>Calendars</SheetTitle>
              </SheetHeader>
              {sidebar}
            </SheetContent>
          </Sheet>
        )}

        {/* Main view */}
        <CalendarViewArea
          view={view}
          currentDate={currentDate}
          visibleEvents={visibleEvents}
          settings={settings}
          loading={loading}
          error={calendarsError}
          onClickDate={handleClickDate}
          onSelectDays={handleSelectDays}
          onShowMore={handleShowMore}
          onClickEvent={handleClickEvent}
          onSelectRange={handleSelectRange}
          resizingEventId={resizingEventId}
          resizeHeight={resizeHeight}
          onResizeStart={onResizeStart}
          draggingEventId={draggingEventId}
          dragOffset={dragOffset}
          onEventDragStart={onEventDragStart}
          monthDraggingEventId={monthDraggingEventId}
          onMonthEventDragStart={onMonthEventDragStart}
          weather={weather}
          hourHeightPx={hourHeightPx}
        />
      </div>

      {/* Dialogs */}
      <RecurringScopeDialog
        open={pendingResize !== null}
        heading="Change recurring event"
        title={pendingResize?.event.title ?? ''}
        legend="Which events should be changed?"
        name="resize-scope"
        confirmLabel="Save"
        onCancel={() => {
          setPendingResize(null);
          // The grid is showing the dragged height optimistically; refetch so a
          // cancelled resize snaps back to the stored duration.
          refetchEvents();
        }}
        onConfirm={(scope) => {
          const pending = pendingResize;
          setPendingResize(null);
          if (pending) void applyResize(pending.event, pending.endAt, scope);
        }}
      />
      <RecurringScopeDialog
        open={pendingMove !== null}
        heading="Move recurring event"
        title={pendingMove?.event.title ?? ''}
        legend="Which events should be moved?"
        name="move-scope"
        confirmLabel="Move"
        onCancel={() => {
          setPendingMove(null);
          // The drop already painted the event at the new spot optimistically;
          // refetch so a cancelled move snaps back.
          refetchEvents();
        }}
        onConfirm={(scope) => {
          const pending = pendingMove;
          setPendingMove(null);
          if (pending) void applyMove(pending.event, pending.startAt, pending.endAt, scope);
        }}
      />
      <CalendarDialogs
        // Event dialog
        eventDialogOpen={eventDialogOpen}
        onCloseEventDialog={() => {
          setEventDialogOpen(false);
          setEditEvent(null);
        }}
        onSaveEvent={handleSaveEvent}
        calendars={calendars}
        editEvent={editEvent}
        initialEventData={initialEventData}
        templates={templates}
        onSaveAsTemplate={editEvent ? handleSaveAsTemplate : undefined}
        eventDialogSettings={eventDialogSettings}
        visibleEvents={visibleEvents}
        // Quick event dialog
        quickEventOpen={quickEventOpen}
        quickEventDraft={quickEventDraft}
        onCloseQuickEvent={() => setQuickEventOpen(false)}
        onQuickSave={handleQuickSave}
        onQuickExpand={handleQuickExpand}
        // Event detail dialog
        detailDialogOpen={detailDialogOpen}
        selectedEvent={selectedEvent}
        onCloseDetailDialog={() => {
          setDetailDialogOpen(false);
          setSelectedEvent(null);
        }}
        onEditEvent={handleEditEvent}
        onDeleteEvent={handleDeleteEvent}
        // Calendar form dialog
        calendarFormOpen={calendarFormOpen}
        onCloseCalendarForm={() => setCalendarFormOpen(false)}
        onSaveCalendar={handleSaveCalendar}
        // Import dialog
        importDialogOpen={importDialogOpen}
        onCloseImport={() => setImportDialogOpen(false)}
        onImportComplete={() => {
          refetchEvents();
          fetchCalendars();
        }}
        onCreateCalendar={() => setCalendarFormOpen(true)}
        // Settings sheet
        settingsOpen={settingsOpen}
        featuresOpen={featuresOpen}
        onCloseFeatures={() => setFeaturesOpen(false)}
        onCloseSettings={() => setSettingsOpen(false)}
        settings={settings}
        onUpdateSettings={updateSettings}
        focusTimeSettings={focusTimeSettings}
        onUpdateFocusTime={updateFocusTimeSettings}
        onGenerateFocusBlocks={() => handleFocusBlocksAction(generateFocusBlocks)}
        onRegenerateFocusBlocks={() => handleFocusBlocksAction(regenerateFocusBlocks)}
        onClearFocusBlocks={() => handleFocusBlocksAction(clearFocusBlocks)}
        // Command palette
        commandPaletteOpen={commandPaletteOpen}
        onCloseCommandPalette={() => setCommandPaletteOpen(false)}
        onNewEvent={handleNewEvent}
        onToday={handleToday}
        onPrev={handlePrev}
        onNext={handleNext}
        onViewChange={handleViewChange}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenImport={() => setImportDialogOpen(true)}
        onGoToDate={handleGoToDate}
        calendarSets={calendarSets}
        onActivateSet={activateSet}
        onNewEventFromTemplate={handleNewEventFromTemplate}
        onManageTemplates={() => setTemplateManagerOpen(true)}
        // Shortcut help
        shortcutHelpOpen={shortcutHelpOpen}
        onCloseShortcutHelp={() => setShortcutHelpOpen(false)}
        // Calendar set manager
        calendarSetManagerOpen={calendarSetManagerOpen}
        onCloseCalendarSetManager={() => setCalendarSetManagerOpen(false)}
        onCreateSet={createSet}
        onRenameSet={renameSet}
        onDeleteSet={deleteCalendarSet}
        onUpdateSetCalendars={updateSetCalendars}
        // Template manager
        templateManagerOpen={templateManagerOpen}
        onCloseTemplateManager={() => setTemplateManagerOpen(false)}
        onCreateTemplate={createTemplate}
        onUpdateTemplate={updateTemplate}
        onDeleteTemplate={deleteTemplate}
        // Share calendar dialog
        shareDialogOpen={shareDialogOpen}
        onCloseShareDialog={() => {
          setShareDialogOpen(false);
          setShareCalendarTarget(null);
        }}
        shareCalendarTarget={shareCalendarTarget}
        publishFeedDialogOpen={publishFeedDialogOpen}
        onClosePublishFeedDialog={() => {
          setPublishFeedDialogOpen(false);
          setPublishFeedTarget(null);
        }}
        publishFeedTarget={publishFeedTarget}
        // Find time dialog
        findTimeOpen={findTimeOpen}
        onCloseFindTime={() => setFindTimeOpen(false)}
        onFindTimeSelectSlot={handleFindTimeSelectSlot}
        // Scheduling links manager
        schedulingLinksOpen={schedulingLinksOpen}
        onCloseSchedulingLinks={() => setSchedulingLinksOpen(false)}
        schedulingLinks={schedulingLinks}
        onCreateLink={createSchedulingLink}
        onUpdateLink={updateSchedulingLink}
        onDeleteLink={deleteSchedulingLink}
        onFetchBookings={fetchSchedulingLinkBookings}
        onCancelBooking={cancelSchedulingLinkBooking}
        // Task dialog
        taskDialogOpen={taskDialogOpen}
        onCloseTaskDialog={() => {
          setTaskDialogOpen(false);
          setEditTaskTarget(null);
        }}
        onSaveTask={handleSaveTask}
        editTaskTarget={editTaskTarget}
        // Schedule suggestions dialog
        suggestionsDialogOpen={suggestionsDialogOpen}
        onCloseSuggestions={() => setSuggestionsDialogOpen(false)}
        suggestionsTask={suggestionsTask}
        suggestions={suggestions}
        suggestionsLoading={suggestionsLoading}
        onAcceptSuggestion={handleAcceptSuggestion}
        // Daily planning wizard
        dailyPlanningOpen={dailyPlanningOpen}
        onCloseDailyPlanning={() => setDailyPlanningOpen(false)}
        dailyPlanning={dailyPlanning}
        onCompleteTask={handleCompleteTask}
        onScheduleTaskToTime={handleScheduleTaskToTime}
        // Subscribe dialog
        subscribeDialogOpen={subscribeDialogOpen}
        onCloseSubscribe={() => setSubscribeDialogOpen(false)}
        onSubscribe={handleSubscribe}
        // Evening review wizard
        eveningReviewOpen={eveningReviewOpen}
        onCloseEveningReview={() => {
          dailyPlanning.dismissEveningReview();
          setEveningReviewOpen(false);
        }}
        eveningReviewDailyPlanning={dailyPlanning}
        tasks={tasks}
        // Focus time dialog
        focusTimeDialogOpen={focusTimeDialogOpen}
        onCloseFocusTimeDialog={() => setFocusTimeDialogOpen(false)}
        onOpenSettingsFromFocusTime={() => setSettingsOpen(true)}
      />
    </div>
  );
}
